import { ThrownServiceError } from '../types/Core/ThrownServiceError'
import { Employee } from '../types/Employee/Employee'
import { ImportResponse } from '../types/Upload/ImportResponse'
import { NewRequest, RequestBacklog } from '../types/Work/Request'
import { createNewRequestFB, getRequestsOfTaskFB } from './CacheFallbackServices/requestFallback'
import { showErrorToast } from './CommonServices/alertServices'
import { deleteOne, getAll, getOne, post } from './CommonServices/httpService'
import { ForemanDB } from '../types/IndexedDB/ForemanDB'
import { getQCsFB } from './CacheFallbackServices/requestFallback'
import { mapRequests } from './CacheMapperServices/requestMapper'
import { hasConnection } from './CommonServices/healthGate'

const createNewRequest = async (
	inspectionRequest: NewRequest,
	db?: ForemanDB | null,
): Promise<void> => {
	if (!(await hasConnection()) && db) {
		try {
			await createNewRequestFB(db, inspectionRequest)
			return
		} catch (error: any) {
			showErrorToast({ message: error.message || 'Failed to create request in offline mode' })
			throw error
		}
	}

	await post(`/Requests`, inspectionRequest)
}

const getQCs = async (extended = false, db?: ForemanDB | null): Promise<Employee[] | null> => {
	if (!(await hasConnection()) && db) {
		try {
			return await getQCsFB(db)
		} catch (error: any) {
			showErrorToast({ message: error.message || 'Failed to get QC workers from cache' })
			return null
		}
	}
	try {
		const result = await getAll<Employee[]>(`/Workers/QC${extended ? '?extended=true' : ''}`)
		return result.data
	} catch (err) {
		const { eventId, message } = err as ThrownServiceError
		showErrorToast({
			eventId: eventId ?? '',
			message: message ?? 'Could not retrieve QC workers for this project',
		})
		return null
	}
}

const getDefaultQC = async (workItemId: number) => {
	if (!(await hasConnection())) return null
	const result = await getOne<number | null>(`/Workers/QC/${workItemId}`)
	return result.data
}

const getRequestsOfTask = async (
	taskId: number,
	taskType: string,
	db: ForemanDB | null,
): Promise<RequestBacklog[] | null> => {
	if (!(await hasConnection()) && db) {
		try {
			return await getRequestsOfTaskFB(db, taskId, taskType)
		} catch (error: any) {
			showErrorToast({ message: error.message || 'Failed to get requests from cache' })
			return null
		}
	}
	try {
		const result = await getAll<RequestBacklog[]>(`/Requests/${taskId}?type=${taskType}`)
		if (db) {
			mapRequests(db, result.data, taskId, taskType)
		}
		return result.data
	} catch (err) {
		return null
	}
}

const getAttachmentsOfRequest = async (requestId: number) => {
	try {
		const result = await getAll<ImportResponse[]>(`/Requests/Attachments/${requestId}`)
		return result.data.map((attach) => `${attach.uploadUri}${attach.sasToken}`)
	} catch (err) {
		return []
	}
}

const withdrawRequest = async (requestId: number) => {
	if (!(await hasConnection())) {
		showErrorToast({
			message: 'No internet connection, this action cannot be completed.',
		})
		return
	}
	await deleteOne(`/Requests/${requestId}`)
}
export {
	createNewRequest,
	getQCs,
	getDefaultQC,
	getRequestsOfTask,
	withdrawRequest,
	getAttachmentsOfRequest,
}
